/**
 * Real-time 3D-LUT video colour grade (WebGL2). DJI footage shot in D-Log M is
 * a flat log profile; applying the official model LUT maps it to Rec.709 so it
 * looks the way it should. The LUT (.cube) is pre-baked to a compact binary
 * (1 byte N, then N³ RGB bytes) and uploaded as a 3D texture; each video frame is
 * drawn through a fragment shader that does the trilinear lookup on the GPU.
 */

const VERT = `#version 300 es
in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = vec2(aPos.x * 0.5 + 0.5, 0.5 - aPos.y * 0.5); // flip Y (video is top-down)
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG = `#version 300 es
precision highp float;
precision highp sampler3D;
in vec2 vUV;
out vec4 outColor;
uniform sampler2D uVideo;
uniform sampler3D uLut;
uniform float uN;        // LUT edge size
uniform float uAmount;   // 0..1 blend (1 = full grade)
void main() {
  vec3 c = texture(uVideo, vUV).rgb;
  // Sample texel centres so the trilinear lookup is correct at the cube edges.
  vec3 lutUV = c * ((uN - 1.0) / uN) + (0.5 / uN);
  vec3 graded = texture(uLut, lutUV).rgb;
  outColor = vec4(mix(c, graded, uAmount), 1.0);
}`;

function compile(gl: WebGL2RenderingContext, type: number, src: string): WebGLShader {
  const sh = gl.createShader(type)!;
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    throw new Error('LUT shader: ' + gl.getShaderInfoLog(sh));
  }
  return sh;
}

export class LutEngine {
  private gl: WebGL2RenderingContext;
  private prog: WebGLProgram;
  private vs: WebGLShader;
  private fs: WebGLShader;
  private vbo: WebGLBuffer;
  private videoTex: WebGLTexture;
  private lutTex: WebGLTexture;
  private uN: WebGLUniformLocation;
  private uAmount: WebGLUniformLocation;
  private loaded = false; // false until a LUT is uploaded → render raw video, not black
  amount = 1;

  constructor(canvas: HTMLCanvasElement) {
    const gl = canvas.getContext('webgl2', { premultipliedAlpha: false, preserveDrawingBuffer: true });
    if (!gl) throw new Error('WebGL2 not available');
    this.gl = gl;

    const prog = gl.createProgram()!;
    this.vs = compile(gl, gl.VERTEX_SHADER, VERT);
    this.fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    gl.attachShader(prog, this.vs);
    gl.attachShader(prog, this.fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error('LUT link: ' + gl.getProgramInfoLog(prog));
    this.prog = prog;
    gl.useProgram(prog);

    // Full-screen triangle.
    this.vbo = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'aPos');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    this.videoTex = gl.createTexture()!;
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.videoTex);
    for (const p of [gl.TEXTURE_WRAP_S, gl.TEXTURE_WRAP_T]) gl.texParameteri(gl.TEXTURE_2D, p, gl.CLAMP_TO_EDGE);
    for (const p of [gl.TEXTURE_MIN_FILTER, gl.TEXTURE_MAG_FILTER]) gl.texParameteri(gl.TEXTURE_2D, p, gl.LINEAR);
    gl.uniform1i(gl.getUniformLocation(prog, 'uVideo'), 0);

    this.lutTex = gl.createTexture()!;
    this.uN = gl.getUniformLocation(prog, 'uN')!;
    this.uAmount = gl.getUniformLocation(prog, 'uAmount')!;
    // Bind the LUT sampler to unit 1 up front so it never aliases uVideo's unit 0
    // (which would sample a 2D texture as sampler3D → black) before load() runs.
    gl.uniform1i(gl.getUniformLocation(prog, 'uLut'), 1);
  }

  /** Load the pre-baked LUT binary and upload it as a 3D texture. */
  async load(url: string): Promise<void> {
    const buf = new Uint8Array(await (await fetch(url)).arrayBuffer());
    const n = buf[0];
    const data = buf.subarray(1); // n³ RGB bytes, R fastest (matches texImage3D order)
    const gl = this.gl;
    gl.useProgram(this.prog);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_3D, this.lutTex);
    for (const p of [gl.TEXTURE_WRAP_S, gl.TEXTURE_WRAP_T, gl.TEXTURE_WRAP_R])
      gl.texParameteri(gl.TEXTURE_3D, p, gl.CLAMP_TO_EDGE);
    for (const p of [gl.TEXTURE_MIN_FILTER, gl.TEXTURE_MAG_FILTER]) gl.texParameteri(gl.TEXTURE_3D, p, gl.LINEAR);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.texImage3D(gl.TEXTURE_3D, 0, gl.RGB8, n, n, n, 0, gl.RGB, gl.UNSIGNED_BYTE, data);
    gl.uniform1f(this.uN, n);
    this.loaded = true;
  }

  /** Draw one video frame through the LUT. Sizes the canvas to the video. */
  render(video: HTMLVideoElement): void {
    this.renderSource(video, video.videoWidth, video.videoHeight);
  }

  /** Grade any texturable source (a live <video>, or a WebCodecs VideoFrame for the
   *  offline export) at its native `w×h`. The graded result is on `gl.canvas`. */
  renderSource(source: TexImageSource, w: number, h: number): void {
    const gl = this.gl;
    if (!w || !h) return;
    const cv = gl.canvas as HTMLCanvasElement;
    if (cv.width !== w || cv.height !== h) {
      cv.width = w;
      cv.height = h;
      gl.viewport(0, 0, w, h);
    }
    gl.useProgram(this.prog);
    // Until a LUT is uploaded, blend 0 → pass the raw video through instead of a
    // black frame (the LUT unit would sample an incomplete 3D texture).
    gl.uniform1f(this.uAmount, this.loaded ? this.amount : 0);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.videoTex);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 0);
    try {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, source);
    } catch {
      return; // frame not ready
    }
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }

  dispose(): void {
    const gl = this.gl;
    gl.deleteTexture(this.videoTex);
    gl.deleteTexture(this.lutTex);
    gl.deleteBuffer(this.vbo);
    gl.deleteProgram(this.prog);
    // deleteProgram only detaches shaders — they must be deleted explicitly.
    gl.deleteShader(this.vs);
    gl.deleteShader(this.fs);
  }
}

/** Available baked LUTs, keyed by the colour mode they correct. Extend per model. */
export const LUTS: Record<string, { url: string; label: string }> = {
  dlog_m: { url: 'lut/dji-mini5-dlogm-rec709.bin', label: 'D-Log M → Rec.709 (Mini 5 Pro)' },
};

/**
 * The clip's colour mode for LUT selection. The embedded `color_md` metadata is authoritative; when it's
 * absent (a bare video, or an SRT lacking the channel) fall back to the filename — but the fallback LUT is
 * MODEL-SPECIFIC (Mini-5 D-Log-M→709), so the match is deliberately narrow (D-017): an explicit `dlog(-)m`
 * token, or the DJI clip pattern `DJI_<timestamp>_<n>_D`. A random `*_D` or `*dlog*` name no longer gets the
 * Mini-5 LUT burned in. (Full cross-camera safety needs a model-identity check — see REVIEW.md.)
 * Single source of truth so the player + settings panel never drift.
 */
export function detectColorMode(metaMode: string, name: string): string {
  if (metaMode) return metaMode;
  const base = (name || '').replace(/\.[^.]+$/, '');
  if (/dlog[_-]?m/i.test(base) || /^dji_\d.*_d$/i.test(base)) return 'dlog_m';
  return '';
}
